import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ASSETS } from "../lib/assets";
import { CountUp } from "./CountUp";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 30000;

export const About = () => {
  const [impact, setImpact] = useState({
    dollars_raised: 0,
    kids_reached: 500,
    programs_run: 12,
    free_percent: 100,
  });
  const [live, setLive] = useState(false);

  useEffect(() => {
    let timer;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/impact`);
        setImpact(res.data);
        setLive(true);
      } catch (e) {
        // keep defaults silently
      } finally {
        timer = setTimeout(load, POLL_MS);
      }
    };
    load();
    return () => clearTimeout(timer);
  }, []);

  const STATS = [
    {
      key: "dollars",
      value: impact.dollars_raised,
      prefix: "$",
      decimals: 0,
      label: "Raised",
    },
    {
      key: "kids",
      value: impact.kids_reached,
      suffix: "+",
      label: "Kids Reached",
    },
    {
      key: "programs",
      value: impact.programs_run,
      label: "Programs Run",
    },
    {
      key: "free",
      value: impact.free_percent,
      suffix: "%",
      label: "Free for Families",
    },
  ];

  return (
    <section id="about" data-testid="about-section" className="relative py-24 md:py-32 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.4em] text-[#C4F542] mb-6">/ 01 — Who We Are</p>
            <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl text-white leading-none">
              WHOSE HOUSE?<br />
              <span className="text-[#C4F542]">OUR HOVSE!</span>
            </h2>
          </div>

          <div className="lg:col-span-7 lg:pt-8">
            <p className="text-xs uppercase tracking-[0.35em] text-[#C4F542] mb-4">Mission Statement</p>
            <p className="text-lg text-zinc-300 leading-relaxed font-body">
              Every child deserves a safe, empowering place to grow — physically, emotionally, and
              socially. Through movement, mentorship, and social-emotional learning (SEL), Our HOVSE
              Foundation equips young people with the confidence, discipline, resilience, and
              leadership skills they need to thrive in life and strengthen their communities.
            </p>
          </div>
        </div>

        {/* Impact Counter */}
        <div className="mt-20">
          <div className="flex items-center justify-between mb-8">
            <p className="text-xs uppercase tracking-[0.4em] text-[#C4F542]">/ Live Impact</p>
            <div className="flex items-center gap-2" data-testid="impact-live-badge">
              <span className={`h-2 w-2 rounded-full ${live ? "bg-[#C4F542] animate-pulse" : "bg-zinc-600"}`} />
              <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-400">
                {live ? "Live" : "Loading"}
              </span>
            </div>
          </div>

          <div className="grid lg:grid-cols-12 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-7 relative overflow-hidden border border-white/10 group"
            >
              <img
                src={ASSETS.team}
                alt="Our Hovse Foundation team"
                className="w-full h-[420px] lg:h-[480px] object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/90 to-transparent p-6">
                <p className="text-xs uppercase tracking-[0.3em] text-[#C4F542] mb-2">The Founders</p>
                <p className="text-white font-body text-lg">Philly Weeden &amp; Mo Tucker</p>
              </div>
            </motion.div>

            <div className="lg:col-span-5 grid grid-cols-2 gap-4">
              {STATS.map((s, i) => (
                <motion.div
                  key={s.key}
                  data-testid={`impact-stat-${s.key}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="bg-[#111111] border border-white/10 p-6 lg:p-8 flex flex-col justify-between min-h-[140px] lg:min-h-[230px] hover:border-[#C4F542]/50 transition-colors"
                >
                  <CountUp
                    value={s.value}
                    prefix={s.prefix || ""}
                    suffix={s.suffix || ""}
                    decimals={s.decimals || 0}
                    className="font-display text-5xl lg:text-6xl text-[#C4F542]"
                  />
                  <span className="text-xs uppercase tracking-[0.25em] text-zinc-400 mt-3">{s.label}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
